// src/components/common/PageTitle.tsx
// Shared page title bar (sticky, primary colour, used across most page types).

import React from 'react';
import { Box } from '@mui/material';
import { WarningAmber as WarningIcon } from '@mui/icons-material';
import {
  FormPageHeader as HeaderBar,
  FormPageTitleBox,
  FormPageTitle,
  FormPageModeText,
  FormPageModeSeparator,
} from '../forms/shared/FormElements';

// Re-export for consumers that need to build custom title content
export const PageTitleText = FormPageTitle;

const WarningIconBox: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <Box
    sx={{
      padding: compact ? '0 0 0 0.5rem' : '0 0.5rem',
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    }}
  >
    <WarningIcon
      sx={{
        fontSize: '2.25rem',
        color: '#fff',
        marginLeft: '0.5rem',
        '@media (min-width: 600px)': {
          marginLeft: '2rem',
        },
      }}
    />
  </Box>
);

const LeftSpacer: React.FC = () => (
  <Box
    sx={{
      width: '1rem',
      '@media (min-width: 600px)': {
        width: '3rem',
      },
    }}
  />
);

interface PageTitleProps {
  title: string | React.ReactNode;
  modeText?: string;
  showSeparator?: boolean;
  leftContent?: React.ReactNode;
  showWarning?: boolean;
  compactWarning?: boolean;
  rightContent?: React.ReactNode;
}

const PageTitle: React.FC<PageTitleProps> = ({
  title,
  modeText,
  showSeparator = true,
  leftContent,
  showWarning = false,
  compactWarning = false,
  rightContent,
}) => {
  const leftArea =
    leftContent !== undefined ? (
      leftContent
    ) : showWarning ? (
      <WarningIconBox compact={compactWarning} />
    ) : (
      <LeftSpacer />
    );

  return (
    <HeaderBar>
      {leftArea}

      <FormPageTitleBox>
        {/* FormPageTitle is a plain styled(Typography) with no heading
            role by default (MUI's default variant, body1, renders a <p>) -
            left every page title unannounced as a heading to assistive
            tech. role="heading" + aria-level={1} is applied here at the
            usage site, matching DialogBase's DialogTitleText fix, rather
            than baked into FormPageTitle's own defaultProps - React 19
            deprecates defaultProps on function/styled components, so this
            is the safe place for it. The other real consumer of
            FormPageTitle (BeingsPage.tsx, via the PageTitleText re-export
            below) carries the same two props for the same reason. */}
        {typeof title === 'string' ? (
          <FormPageTitle role="heading" aria-level={1}>
            {title}
          </FormPageTitle>
        ) : (
          title
        )}
        {showSeparator && modeText && (
          <FormPageModeSeparator>/</FormPageModeSeparator>
        )}
        {modeText && <FormPageModeText>{modeText}</FormPageModeText>}
      </FormPageTitleBox>

      {rightContent}
    </HeaderBar>
  );
};

export default PageTitle;
